import { resolveStoredFileUrl } from "utils/storedFileUrl";
// React
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useState } from "react";

// @mui material components
import { Card, CircularProgress, Fade, Grid, Icon, Modal, Tab, Tabs, Tooltip } from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import DataTable from "components/Tables/Table-v2";
import { formatMoneyPunto } from "utils/formatters";
import { getUproColor } from "utils/colors";
import SoftButton from "components/SoftButton";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { formatDate } from "utils/formatters";
import ModalHistorial from "./historial";
import dayjs from "dayjs";
import { mensajeError } from "layouts/compras/mensajeError";
import { nombreCompraDetalle, unidadCompraDetalle } from "utils/presentacionCompra";

// La prórroga suma 10 días a la fecha más tardía entre el vencimiento actual y hoy
// (el backend calcula la misma fecha al guardar).
const vencimientoProrrogado = (fechaVencimiento) => {
  const vencimiento = dayjs(fechaVencimiento);
  const desde = vencimiento.isValid() && vencimiento.isAfter(dayjs()) ? vencimiento : dayjs();
  return desde.add(10, "day");
};
import Loading from "components/Carteles/Loading";
import { UserContext } from "context/user";
import { PERMISOS_COMPRAS, tienePermisoCompras } from "layouts/compras/permisos";

const PARTIAL_CLOSE_STATE = "Cerrado con entrega parcial";
const COMPLETED_WITH_PARTIAL_DELIVERY_STATE = "Completada con entrega parcial";
const COMPLETED_WITHOUT_DELIVERIES_STATE = "Completada sin entregas";
const PROVISION_TERMINAL_STATES = [
  "Entregada",
  PARTIAL_CLOSE_STATE,
  COMPLETED_WITH_PARTIAL_DELIVERY_STATE,
  COMPLETED_WITHOUT_DELIVERIES_STATE,
];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  width: { xs: "calc(100% - 24px)", sm: "90%", md: "82%", lg: "72%" },
  maxWidth: "980px",
  maxHeight: { xs: "calc(100vh - 24px)", sm: "calc(100vh - 40px)" },
  overflow: "hidden",
  borderRadius: "16px",
  border: "1px solid #E7EBF3",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 24px 70px rgba(20, 34, 66, 0.22)",
  outline: "none",
};

const sectionStyle = {
  border: "1px solid #E7EBF3",
  borderRadius: "12px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 2px 8px rgba(20, 34, 66, 0.035)",
};

const iconButtonStyle = {
  width: { xs: 38, sm: 42 },
  minWidth: { xs: 38, sm: 42 },
  height: { xs: 38, sm: 42 },
  minHeight: { xs: 38, sm: 42 },
  borderRadius: "50%",
  boxShadow: "0 5px 14px rgba(20, 34, 66, 0.12)",
  transition: "background-color 180ms ease, box-shadow 180ms ease",
  "&:hover": { boxShadow: "0 7px 18px rgba(20, 34, 66, 0.18)" },
  "&:focus-visible": { outline: "3px solid rgba(240, 5, 122, 0.24)", outlineOffset: "2px" },
};

export default function ModalProvision({ open, handleClose, id, refetchParent, mode }) {
  const { confirmar } = useContext(ConfirmarContext);
  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);
  const puedeEnviar = mode !== "Lectura" && tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.ENVIAR_ORDEN);
  const puedeProrrogar = mode !== "Lectura" && tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.PRORROGAR_ORDEN);
  const [openHistorial, setOpenHistorial] = useState(false);
  const [emailEnviado, setEmailEnviado] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [provisionExistente, setProvisionExistente] = useState(null);

  const [getProvision, { loading: loadingQuery }] = useLazyQuery(
    gql`
      query getProvisionModalProvision($id: ID!) {
        provisiones(filter: { id: $id }) {
          id
          presupuesto
          anulada
          motivoAnulacion
          createdAt
          fechaVencimiento
          estado
          enviado
          fechaEnvio
          pedidoCompra {
            id
            detalles {
              id
              numero
              cantidad
              cantidadVerificada
              nombreServicio
              nombreCompra
              unidadNombre
              unidadAbreviatura
              requierePack
              unidadPack
              materiaPrima {
                id
                nombre
                nombreCompra
                tipo
                tipoUnidad {
                  abreviatura
                }
              }
            }
          }
          detalles {
            id
            precioUnitario
            cantidad
            cantidadEntregada
            idPedidoCompraDetalle
            materiaPrima {
              id
              nombre
              nombreCompra
              tipo
              tipoUnidad {
                abreviatura
              }
            }
            pedidoCompraDetalle {
              numero
              nombreCompra
              unidadNombre
              unidadAbreviatura
              nombreServicio
              requierePack
              unidadPack
            }
          }
          proveedor {
            id
            nombre
            apellido
            razonSocial
          }
          historial {
            evento
            motivo
            fecha
          }
          ingresos {
            id
            tipo
            fechaEntrega
            observaciones
            createdAt
            movimientoDetalles {
              id
              cantidad
              idProvisionDetalle
              materiaPrima {
                nombre
              }
            }
          }
          total
          totalAdjudicado
          totalRecibido
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      onCompleted: (res) => {
        const data = res?.provisiones?.[0];
        if (data) {
          setProvisionExistente(data);
        }
      },
    }
  );

  const [saveProvision] = useMutation(
    gql`
      mutation saveProvisionModalProvision($input: ProvisionInput!) {
        saveProvision(input: $input) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        handleSnackbar("Cambios guardados correctamente", "success");
        refetchParent();
        handleClose();
      },
      onError: (error) => {
        handleSnackbar(mensajeError(error), "error");
      },
    }
  );

  const [enviarNotificacionOP, { loading: loadingNotificacion }] = useMutation(
    gql`
      mutation enviarNotificacionOP($idPedidoCompra: ID!, $idProvision: ID) {
        enviarNotificacionOP(idPedidoCompra: $idPedidoCompra, idProvision: $idProvision) {
          success
          message
        }
      }
    `,
    {
      onCompleted: (data) => {
        if (data.enviarNotificacionOP.success) {
          setEmailEnviado(true);
          handleSnackbar(data.enviarNotificacionOP.message, "success");
          // Refetch para actualizar el estado de enviado
          refetchParent();
        } else {
          handleSnackbar("Error al enviar notificación", "error");
        }
      },
      onError: (error) => {
        handleSnackbar(mensajeError(error, "Error al enviar notificación"), "error");
      },
    }
  );

  const [cerrarProvisionEntregaParcial, { loading: loadingCierre }] = useMutation(gql`
    mutation cerrarProvisionEntregaParcialModal($idProvision: ID!, $motivo: String!) {
      cerrarProvisionEntregaParcial(idProvision: $idProvision, motivo: $motivo) {
        estado
      }
    }
  `);

  const [completarYCerrarProvision, { loading: loadingCompletar }] = useMutation(gql`
    mutation completarYCerrarProvisionModal($idProvision: ID!, $motivo: String!) {
      completarYCerrarProvision(idProvision: $idProvision, motivo: $motivo) {
        estado
      }
    }
  `);

  const puedeCerrar =
    user?.tipoUser === "Administrador" ||
    user?.permisos?.some((permiso) => permiso.nombre === "Cerrar orden de provisión");
  const estadoCerrable = provisionExistente?.estado === "Entrega parcial";
  const esTerminal = PROVISION_TERMINAL_STATES.includes(provisionExistente?.estado);
  const puedeAnular = Boolean(
    provisionExistente &&
      !provisionExistente.anulada &&
      mode !== "Lectura" && tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.ANULAR_ORDEN) &&
      provisionExistente.estado !== "Entrega parcial" &&
      !esTerminal
  );
  const estadoCompletable = provisionExistente && !provisionExistente.anulada && !esTerminal;
  const cierreTerminal = provisionExistente?.historial?.find(
    (item) => item.evento === provisionExistente?.estado
  );
  const estadoVisual = (() => {
    if (provisionExistente?.anulada) {
      return { background: "#FDECEC", color: "#D9363E", dot: "#EF4444" };
    }
    if (esTerminal) {
      return { background: "#EAF8F0", color: "#159455", dot: "#22C55E" };
    }
    if (provisionExistente?.estado === "Vencida") {
      return { background: "#FDECEC", color: "#D9363E", dot: "#EF4444" };
    }
    return { background: "#FFF2DE", color: "#ED7A14", dot: "#F59E0B" };
  })();

  const refreshAfterClosure = async (message) => {
    const { data } = await getProvision({
      variables: { id },
      fetchPolicy: "network-only",
    });
    const updatedProvision = data?.provisiones?.[0];
    if (updatedProvision) {
      setProvisionExistente(updatedProvision);
    }
    await refetchParent();
    handleSnackbar(message, "success");
  };

  const cerrarEntregaParcial = () => {
    confirmar({
      icon: "warning",
      title: "Cerrar con entrega parcial",
      message:
        "La OP quedará cerrada y las cantidades pendientes volverán a estar disponibles para crear una nueva orden de provisión. Ingrese el motivo del cierre.",
      pedirMotivo: true,
      func: async (motivo) => {
        try {
          await cerrarProvisionEntregaParcial({
            variables: {
              idProvision: provisionExistente?.id,
              motivo,
            },
          });
          await refreshAfterClosure(
            "Orden cerrada con entrega parcial; el saldo pendiente está disponible para una nueva OP"
          );
        } catch (error) {
          handleSnackbar(mensajeError(error), "error");
        }
      },
    });
  };

  const marcarCompletoYCerrar = () => {
    confirmar({
      icon: "warning",
      title: "Marcar completo y cerrar",
      message:
        "La OP quedará cerrada definitivamente. Los saldos pendientes de sus ítems se desestimarán y no podrán utilizarse para crear otra orden de provisión. Ingrese el motivo del cierre.",
      pedirMotivo: true,
      func: async (motivo) => {
        try {
          const { data } = await completarYCerrarProvision({
            variables: {
              idProvision: provisionExistente?.id,
              motivo,
            },
          });
          await refreshAfterClosure(
            `Orden cerrada como "${data?.completarYCerrarProvision?.estado}"; el saldo pendiente fue desestimado`
          );
        } catch (error) {
          handleSnackbar(mensajeError(error), "error");
        }
      },
    });
  };

  const [columns] = useState([
    { name: "index", desc: "item", width: "1%", align: "center" },
    { name: "id", desc: "Código", width: "1%", align: "center" },
    { name: "producto", desc: "Producto", align: "left" },
    { name: "cantidad", desc: "Cantidad solicitada", align: "center" },
    { name: "cantidadEntregada", desc: "Cantidad recibida", align: "center" },
    { name: "precio", desc: "Precio unitario", align: "center" },
    { name: "subtotal", desc: "Subtotal adjudicado", align: "right" },
  ]);

  const [pedidoColumns] = useState([
    { name: "index", desc: "item", width: "1%", align: "center" },
    { name: "id", desc: "Código", width: "1%", align: "center" },
    { name: "producto", desc: "Producto", align: "left" },
    { name: "cantidad", desc: "Cantidad del pedido", align: "center" },
    { name: "enProvision", desc: "En esta OP", align: "center" },
  ]);

  const [remitosColumns] = useState([
    { name: "id", desc: "N° Remito", width: "12%", align: "center" },
    { name: "fecha", desc: "Fecha", width: "16%", align: "center" },
    { name: "items", desc: "Items", width: "12%", align: "center" },
    { name: "observaciones", desc: "Observaciones", align: "left" },
    { name: "acciones", desc: "Acciones", width: "12%", align: "center" },
  ]);

  useEffect(() => {
    if (open && id) {
      setActiveTab(0);
      getProvision({ variables: { id } });
      setEmailEnviado(false); // Reset local status
    } else {
      setProvisionExistente(null);
      setEmailEnviado(false);
    }
  }, [open, id, getProvision]);

  const getRows = useCallback((data) => {
    return data?.map((item, index) => {
      const unidad = unidadCompraDetalle(item.pedidoCompraDetalle, item.materiaPrima, true);

      return {
        hasBorder: true,
        index: (
          <SoftBox
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            width="26px"
            height="26px"
            sx={{ borderRadius: "50%", backgroundColor: "#F3F5F9", color: "#53627A" }}
          >
            {item.pedidoCompraDetalle?.numero || index + 1}
          </SoftBox>
        ),
        id: (
          <SoftBox display="flex" gap={1} color="secondary">
            <Icon
              sx={{
                color:
                  item.materiaPrima?.tipo === "Material"
                    ? getUproColor("uproYellow")
                    : item.materiaPrima?.tipo === "Consumible"
                    ? getUproColor("uproGreen")
                    : getUproColor("uproPink"),
              }}
              fontSize="small"
            >
              {item.materiaPrima?.tipo === "Material"
                ? "category"
                : item.materiaPrima?.tipo === "Consumible"
                ? "recycling"
                : "layers"}
            </Icon>
            {item.materiaPrima?.id}
          </SoftBox>
        ),
        producto: (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftTypography variant="caption" fontWeight="bold">
              {nombreCompraDetalle(item.pedidoCompraDetalle, item.materiaPrima)}
            </SoftTypography>
          </SoftBox>
        ),
        cantidad: (
          <SoftBox display="inline-flex" alignItems="baseline" gap={0.5}>
            <SoftTypography variant="button">{formatMoneyPunto(item.cantidad || 0)}</SoftTypography>
            {unidad && (
              <SoftTypography variant="caption" color="secondary" fontWeight="bold">
                {unidad}
              </SoftTypography>
            )}
          </SoftBox>
        ),
        cantidadEntregada: (
          <SoftBox display="inline-flex" alignItems="baseline" gap={0.5}>
            <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#159455" }}>
              {formatMoneyPunto(item.cantidadEntregada || 0)}
            </SoftTypography>
            {unidad && (
              <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#159455" }}>
                {unidad}
              </SoftTypography>
            )}
          </SoftBox>
        ),
        precio: <SoftBox>${formatMoneyPunto(item.precioUnitario || 0)}</SoftBox>,
        subtotal: (
          <SoftTypography variant="button" fontWeight="bold" color="dark">
            ${formatMoneyPunto((item.precioUnitario || 0) * (item.cantidad || 0))}
          </SoftTypography>
        ),
      };
    });
  }, []);

  const getPedidoRows = useCallback((data, detallesProvision) => {
    const detallesEnProvision = new Set(
      (detallesProvision || []).map((detalle) => String(detalle.idPedidoCompraDetalle))
    );

    return data?.map((item, index) => {
      const perteneceAProvision = detallesEnProvision.has(String(item.id));
      const unidad = unidadCompraDetalle(item, item.materiaPrima, true);

      return {
        hasBorder: true,
        index: (
          <SoftBox
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            width="26px"
            height="26px"
            sx={{ borderRadius: "50%", backgroundColor: "#F3F5F9", color: "#53627A" }}
          >
            {item.numero || index + 1}
          </SoftBox>
        ),
        id: (
          <SoftBox display="flex" gap={1} color="secondary">
            <Icon
              sx={{
                color:
                  item.materiaPrima?.tipo === "Material"
                    ? getUproColor("uproYellow")
                    : item.materiaPrima?.tipo === "Consumible"
                    ? getUproColor("uproGreen")
                    : getUproColor("uproPink"),
              }}
              fontSize="small"
            >
              {item.materiaPrima?.tipo === "Material"
                ? "category"
                : item.materiaPrima?.tipo === "Consumible"
                ? "recycling"
                : "layers"}
            </Icon>
            {item.materiaPrima?.id || "—"}
          </SoftBox>
        ),
        producto: (
          <SoftTypography variant="caption" fontWeight="bold">
            {nombreCompraDetalle(item)}
          </SoftTypography>
        ),
        cantidad: (
          <SoftBox display="inline-flex" alignItems="baseline" gap={0.5}>
            <SoftTypography variant="button">
              {formatMoneyPunto(item.cantidadVerificada ?? item.cantidad ?? 0)}
            </SoftTypography>
            {unidad && (
              <SoftTypography variant="caption" color="secondary" fontWeight="bold">
                {unidad}
              </SoftTypography>
            )}
          </SoftBox>
        ),
        enProvision: (
          <SoftBox
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            px={1}
            py={0.35}
            sx={{
              borderRadius: "999px",
              color: perteneceAProvision ? "#159455" : "#53627A",
              backgroundColor: perteneceAProvision ? "#EAF8F0" : "#F3F5F9",
            }}
          >
            <Icon sx={{ fontSize: "0.9rem !important" }}>
              {perteneceAProvision ? "check_circle" : "remove_circle_outline"}
            </Icon>
            <SoftTypography variant="caption" fontWeight="bold" color="inherit">
              {perteneceAProvision ? "Sí" : "No"}
            </SoftTypography>
          </SoftBox>
        ),
      };
    });
  }, []);

  const getRemitoRows = useCallback((data) => {
    return data?.map((remito) => ({
      hasBorder: true,
      id: (
        <SoftBox
          display="inline-flex"
          px={1}
          py={0.35}
          sx={{ borderRadius: "999px", color: "#1672F3", backgroundColor: "#EAF2FF" }}
        >
          <SoftTypography variant="caption" fontWeight="bold" color="inherit">
            #{remito.id}
          </SoftTypography>
        </SoftBox>
      ),
      fecha: (
        <SoftTypography variant="caption" fontWeight="medium">
          {formatDate(remito.fechaEntrega || remito.createdAt, "dd/MM/yyyy")}
        </SoftTypography>
      ),
      items: remito.movimientoDetalles?.length || 0,
      observaciones: (
        <SoftTypography
          variant="caption"
          fontWeight="medium"
          title={remito.observaciones || ""}
          sx={{
            display: "block",
            maxWidth: "320px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {remito.observaciones || "-"}
        </SoftTypography>
      ),
      acciones: (
        <Tooltip title="Descargar PDF" placement="top">
          <SoftButton
            aria-label={`Descargar remito ${remito.id}`}
            variant="outlined"
            color="primary"
            size="small"
            iconOnly
            circular
            onClick={() =>
              window.open(`/remitoIndividual/pdf/${remito.id}`, "_blank", "noopener,noreferrer")
            }
            sx={{ width: 30, minWidth: 30, height: 30, minHeight: 30 }}
          >
            <Icon fontSize="small">picture_as_pdf</Icon>
          </SoftButton>
        </Tooltip>
      ),
    }));
  }, []);

  const anularProvision = async () => {
    confirmar({
      icon: "cancel",
      title: "Anular orden de provisión",
      message: "¿Está seguro que desea anular la orden? debe ingresar motivo de anulación.",
      pedirMotivo: true,
      func: (motivo) => {
        saveProvision({
          variables: {
            input: {
              id: provisionExistente?.id,
              anulada: true,
              motivoAnulacion: motivo,
            },
          },
        });
      },
    });
  };

  const prorrogarProvision = async (motivo) => {
    const vencimiento = vencimientoProrrogado(provisionExistente?.fechaVencimiento).format("YYYY-MM-DD HH:mm:ss");
    saveProvision({
      variables: {
        input: {
          id: provisionExistente?.id,
          fechaVencimiento: vencimiento,
          motivoProrroga: motivo,
        },
      },
    });
  };

  const enviarNotificacion = async () => {
    if (!puedeEnviar) return;
    if (!provisionExistente?.pedidoCompra?.id) {
      handleSnackbar("Error: No se encontró el ID del pedido", "error");
      return;
    }

    confirmar({
      icon: "email",
      title: "Enviar notificación de OP",
      message:
        "¿Está seguro que desea enviar la notificación de la orden de provisión al proveedor por email? Se incluirá un enlace para descargar el PDF de la orden.",
      func: () => {
        enviarNotificacionOP({
          variables: {
            idPedidoCompra: provisionExistente?.pedidoCompra.id,
            idProvision: provisionExistente?.id,
          },
        });
      },
    });
  };

  const detallesPedido = provisionExistente?.pedidoCompra?.detalles || [];
  const remitosProvision = (provisionExistente?.ingresos || []).filter(
    (movimiento) => movimiento.tipo === "Remito Proveedor"
  );
  const totalOrden = (provisionExistente?.detalles || []).reduce(
    (total, detalle) =>
      total + (Number(detalle.precioUnitario) || 0) * (Number(detalle.cantidad) || 0),
    0
  );

  return (
    <Modal
      aria-labelledby="provision-modal-title"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            component="header"
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={2}
            px={{ xs: 1.5, sm: 2.5 }}
            py={{ xs: 1.5, sm: 2 }}
            sx={{ zIndex: 2, borderBottom: "1px solid #EEF1F6", flexShrink: 0 }}
          >
            <SoftBox display="flex" gap={{ xs: 1.25, sm: 2 }} alignItems="center" minWidth={0}>
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: { xs: 42, sm: 48 },
                  height: { xs: 42, sm: 48 },
                  flexShrink: 0,
                  borderRadius: "12px",
                  color: "#1672F3",
                  backgroundColor: "#EAF2FF",
                }}
              >
                {loadingQuery ? (
                  <CircularProgress size={22} sx={{ color: "#1672F3" }} />
                ) : (
                  <Icon>assignment</Icon>
                )}
              </SoftBox>
              <SoftBox minWidth={0}>
                <SoftTypography
                  id="provision-modal-title"
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    color: "#17233C",
                    lineHeight: 1.2,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  {loadingQuery
                    ? "Cargando orden"
                    : `Orden de provisión #${provisionExistente?.id || "—"}`}
                </SoftTypography>
                {!loadingQuery && provisionExistente && (
                  <SoftBox
                    display="inline-flex"
                    alignItems="center"
                    gap={0.75}
                    mt={0.6}
                    px={1.15}
                    py={0.35}
                    sx={{ borderRadius: "999px", backgroundColor: estadoVisual.background }}
                  >
                    <SoftBox
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: estadoVisual.dot,
                      }}
                    />
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: estadoVisual.color }}
                    >
                      {provisionExistente?.estado || "Sin estado"}
                    </SoftTypography>
                  </SoftBox>
                )}
              </SoftBox>
            </SoftBox>
            <SoftBox
              display="flex"
              justifyContent={{ xs: "space-between", sm: "flex-end" }}
              alignItems="center"
              gap={{ xs: 0.6, sm: 1 }}
            >
              <Tooltip placement="top" title="Ver historial de la orden">
                <span>
                  <SoftButton
                    aria-label="Ver historial de la orden"
                    variant="gradient"
                    color="uproYellow"
                    iconOnly
                    circular
                    onClick={() => setOpenHistorial(true)}
                    disabled={!provisionExistente}
                    sx={iconButtonStyle}
                  >
                    <Icon>history</Icon>
                  </SoftButton>
                </span>
              </Tooltip>
              <Tooltip
                placement="top"
                title={
                  emailEnviado || provisionExistente?.enviado
                    ? "Email enviado"
                    : "Enviar notificación por email"
                }
              >
                <span>
                  <SoftButton
                    aria-label={
                      emailEnviado || provisionExistente?.enviado
                        ? "Email enviado"
                        : "Enviar notificación por email"
                    }
                    variant="gradient"
                    color="uproGreen"
                    iconOnly
                    circular
                    onClick={enviarNotificacion}
                    disabled={
                      !puedeEnviar ||
                      loadingQuery ||
                      !provisionExistente?.id ||
                      provisionExistente?.anulada ||
                      esTerminal ||
                      loadingNotificacion ||
                      emailEnviado ||
                      provisionExistente?.enviado
                    }
                    sx={iconButtonStyle}
                  >
                    {loadingNotificacion ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Icon>
                        {emailEnviado || provisionExistente?.enviado ? "mark_email_read" : "email"}
                      </Icon>
                    )}
                  </SoftButton>
                </span>
              </Tooltip>
              <Tooltip placement="top" title="Descargar reporte PDF">
                <span>
                  <SoftButton
                    aria-label="Descargar reporte PDF"
                    variant="gradient"
                    color="primary"
                    iconOnly
                    circular
                    onClick={() => window.open(`/op/pdf/${provisionExistente?.id ?? 0}`)}
                    disabled={!provisionExistente}
                    sx={iconButtonStyle}
                  >
                    <Icon>download</Icon>
                  </SoftButton>
                </span>
              </Tooltip>
              <Tooltip placement="top" title="Cerrar">
                <span>
                  <SoftButton
                    aria-label="Cerrar modal"
                    variant="gradient"
                    color="uproPink"
                    iconOnly
                    circular
                    onClick={handleClose}
                    sx={iconButtonStyle}
                  >
                    <Icon>close</Icon>
                  </SoftButton>
                </span>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <SoftBox
            px={{ xs: 1.5, sm: 2.5 }}
            py={{ xs: 1.5, sm: 2 }}
            sx={{ overflowY: "auto", backgroundColor: "#FBFCFE" }}
          >
            {loadingQuery && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Loading />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Loading />
                </Grid>
                <Grid item xs={12}>
                  <Loading />
                </Grid>
              </Grid>
            )}
            {provisionExistente && !loadingQuery && (
              <Grid container spacing={1.25}>
                <Grid item xs={12}>
                  <Grid
                    container
                    rowSpacing={{ xs: 0.75, lg: 0 }}
                    p={{ xs: 1, sm: 1.25 }}
                    sx={sectionStyle}
                  >
                    <Grid item xs={12} sm={6} lg={3.5}>
                      <SoftBox
                        p={1}
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{ height: "100%" }}
                      >
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            mt: 0.25,
                            borderRadius: "50%",
                            color: "#3568D4",
                            backgroundColor: "#EDF3FF",
                          }}
                        >
                          <Icon>people_outline</Icon>
                        </SoftBox>
                        <SoftBox minWidth={0}>
                          <SoftTypography
                            variant="caption"
                            color="secondary"
                            display="block"
                            mb={0.2}
                          >
                            Proveedor
                          </SoftTypography>
                          <SoftTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: "#17233C", lineHeight: 1.35, overflowWrap: "anywhere" }}
                          >
                            {[
                              provisionExistente?.proveedor?.razonSocial,
                              provisionExistente?.proveedor?.nombre,
                              provisionExistente?.proveedor?.apellido,
                            ]
                              .filter(Boolean)
                              .join(" ") || "Sin proveedor"}
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={3}
                      sx={{ borderLeft: { sm: "1px solid #E7EBF3" } }}
                    >
                      <SoftBox
                        p={1}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        sx={{ height: "100%" }}
                      >
                        <SoftBox
                          display="flex"
                          alignItems="flex-start"
                          gap={1}
                          minWidth={0}
                          flex={1}
                        >
                          <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              width: 36,
                              height: 36,
                              flexShrink: 0,
                              mt: 0.25,
                              borderRadius: "50%",
                              color: "#8559DD",
                              backgroundColor: "#F1EAFE",
                            }}
                          >
                            <Icon>insert_drive_file</Icon>
                          </SoftBox>
                          <SoftBox minWidth={0}>
                            <SoftTypography
                              variant="caption"
                              color="secondary"
                              display="block"
                              mb={0.2}
                            >
                              Archivo adjunto
                            </SoftTypography>
                            <SoftTypography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                              title={provisionExistente?.presupuesto?.split("\\").pop()}
                              sx={{
                                color: "#53627A",
                                lineHeight: 1.3,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {provisionExistente?.presupuesto?.split("\\").pop() ||
                                "No hay archivo cargado"}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                        <Tooltip title="Ver archivo adjunto" placement="top">
                          <span>
                            <SoftButton
                              aria-label="Ver archivo adjunto"
                              disabled={!provisionExistente?.presupuesto}
                              variant="outlined"
                              color="uproYellow"
                              size="small"
                              iconOnly
                              circular
                              onClick={() => {
                                if (provisionExistente?.presupuesto)
                                  window.open(
                                    resolveStoredFileUrl(provisionExistente.presupuesto),
                                    "_blank"
                                  );
                              }}
                              sx={{
                                width: 30,
                                minWidth: 30,
                                height: 30,
                                minHeight: 30,
                                "&:focus-visible": {
                                  outline: "3px solid rgba(245, 155, 36, 0.24)",
                                  outlineOffset: "2px",
                                },
                              }}
                            >
                              <Icon>visibility</Icon>
                            </SoftButton>
                          </span>
                        </Tooltip>
                      </SoftBox>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={3}
                      sx={{ borderLeft: { lg: "1px solid #E7EBF3" } }}
                    >
                      <SoftBox
                        p={1}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        sx={{ height: "100%" }}
                      >
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            mt: 0.25,
                            borderRadius: "50%",
                            color: "#1672F3",
                            backgroundColor: "#EDF5FF",
                          }}
                        >
                          <Icon>calendar_today</Icon>
                        </SoftBox>
                        <SoftBox minWidth={0}>
                          <SoftTypography
                            variant="caption"
                            color="secondary"
                            display="block"
                            mb={0.2}
                          >
                            Creación
                          </SoftTypography>
                          <SoftTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: "#17233C", lineHeight: 1.25 }}
                          >
                            {provisionExistente?.createdAt
                              ? formatDate(provisionExistente.createdAt, "dd/MM/yy")
                              : "No definida"}
                          </SoftTypography>
                          {provisionExistente?.createdAt && (
                            <SoftTypography
                              variant="caption"
                              fontWeight="bold"
                              display="block"
                              sx={{ color: "#53627A", lineHeight: 1.2 }}
                            >
                              {formatDate(provisionExistente.createdAt, "hh:mm")}
                            </SoftTypography>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={2.5}
                      sx={{ borderLeft: { sm: "1px solid #E7EBF3" } }}
                    >
                      <SoftBox
                        p={1}
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        sx={{ height: "100%" }}
                      >
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            mt: 0.25,
                            borderRadius: "50%",
                            color: "#F27A16",
                            backgroundColor: "#FFF3E8",
                          }}
                        >
                          <Icon>event_busy</Icon>
                        </SoftBox>
                        <SoftBox minWidth={0} flex={1}>
                          <SoftTypography
                            variant="caption"
                            color="secondary"
                            display="block"
                            mb={0.2}
                          >
                            Vencimiento
                          </SoftTypography>
                          <SoftTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: "#17233C", whiteSpace: "nowrap" }}
                          >
                            {provisionExistente?.fechaVencimiento
                              ? formatDate(provisionExistente.fechaVencimiento, "dd/MM/yy")
                              : "Sin vencimiento (entregas a demanda)"}
                          </SoftTypography>
                        </SoftBox>
                        {puedeProrrogar &&
                          provisionExistente?.fechaVencimiento &&
                          !provisionExistente?.anulada &&
                          !esTerminal && (
                            <Tooltip title="Prorrogar provisión 10 días" placement="top">
                              <span>
                                <SoftButton
                                  aria-label="Prorrogar provisión 10 días"
                                  variant="outlined"
                                  color="uproYellow"
                                  size="small"
                                  iconOnly
                                  circular
                                  onClick={() => {
                                    const vencimiento = vencimientoProrrogado(
                                      provisionExistente?.fechaVencimiento
                                    ).format("DD/MM/YYYY");
                                    confirmar({
                                      title: "Prorrogar provisión",
                                      message: `¿Desea prorrogar la provisión por 10 días corridos hasta el ${vencimiento}? Debe ingresar un motivo de prórroga`,
                                      icon: "warning",
                                      pedirMotivo: true,
                                      func: prorrogarProvision,
                                    });
                                  }}
                                  sx={{
                                    width: 30,
                                    minWidth: 30,
                                    height: 30,
                                    minHeight: 30,
                                    "&:focus-visible": {
                                      outline: "3px solid rgba(245, 155, 36, 0.24)",
                                      outlineOffset: "2px",
                                    },
                                  }}
                                >
                                  <Icon>event_repeat</Icon>
                                </SoftButton>
                              </span>
                            </Tooltip>
                          )}
                      </SoftBox>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox sx={{ ...sectionStyle, overflow: "hidden" }}>
                    <Tabs
                      value={activeTab}
                      onChange={(_event, value) => setActiveTab(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      aria-label="Información relacionada con la orden de provisión"
                      sx={{
                        px: { xs: 0.5, sm: 1.25 },
                        borderBottom: "1px solid #E7EBF3",
                        backgroundColor: "#F8FAFD",
                        "& .MuiTab-root": {
                          minHeight: 52,
                          px: { xs: 1, sm: 1.75 },
                          textTransform: "none",
                          color: "#53627A",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          transition: "color 180ms ease, background-color 180ms ease",
                        },
                        "& .MuiTab-root:hover": { color: "#1672F3", backgroundColor: "#F1F6FF" },
                        "& .Mui-selected": { color: "#1672F3 !important" },
                        "& .MuiTabs-indicator": { height: 3, backgroundColor: "#1672F3" },
                      }}
                    >
                      <Tab
                        id="provision-tab-0"
                        aria-controls="provision-tabpanel-0"
                        icon={<Icon fontSize="small">inventory_2</Icon>}
                        iconPosition="start"
                        label="Productos de la orden"
                      />
                      <Tab
                        id="provision-tab-1"
                        aria-controls="provision-tabpanel-1"
                        icon={<Icon fontSize="small">shopping_cart</Icon>}
                        iconPosition="start"
                        label="Productos del pedido"
                      />
                      <Tab
                        id="provision-tab-2"
                        aria-controls="provision-tabpanel-2"
                        icon={<Icon fontSize="small">receipt_long</Icon>}
                        iconPosition="start"
                        label={`Remitos de la OP (${remitosProvision.length})`}
                      />
                    </Tabs>

                    <SoftBox
                      role="tabpanel"
                      id="provision-tabpanel-0"
                      aria-labelledby="provision-tab-0"
                      hidden={activeTab !== 0}
                      p={{ xs: 1.25, sm: 1.5 }}
                    >
                      {activeTab === 0 && (
                        <SoftBox display="flex" flexDirection="column" gap={1.5}>
                          <SoftBox
                            sx={{
                              overflowX: "auto",
                              border: "1px solid #E9EDF4",
                              borderRadius: "9px",
                              "& table": { minWidth: "900px" },
                              "& thead": { backgroundColor: "#F6F8FB" },
                              "& tbody tr:last-of-type td": { borderBottom: 0 },
                              "& tbody tr:hover": { backgroundColor: "#FAFBFD" },
                            }}
                          >
                            <DataTable
                              columns={columns}
                              rows={getRows(provisionExistente?.detalles || [])}
                            />
                          </SoftBox>
                          <SoftBox
                            alignSelf={{ xs: "stretch", sm: "flex-end" }}
                            width={{ xs: "100%", sm: "280px" }}
                            p={1.25}
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                              borderRadius: "12px",
                              background: "linear-gradient(135deg, #F3FBF7 0%, #EAF8F0 100%)",
                            }}
                          >
                            <SoftBox
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                                borderRadius: "50%",
                                color: "#159455",
                                backgroundColor: "#DDF5E8",
                              }}
                            >
                              <Icon>paid</Icon>
                            </SoftBox>
                            <SoftBox>
                              <SoftTypography variant="caption" color="secondary">
                                Importe adjudicado
                              </SoftTypography>
                              <SoftTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ color: "#17233C" }}
                              >
                                ${formatMoneyPunto(totalOrden)}
                              </SoftTypography>
                              <SoftTypography variant="caption" display="block">
                                Recibido: ${formatMoneyPunto(provisionExistente?.totalRecibido || 0)}
                              </SoftTypography>
                              <SoftTypography variant="caption" display="block">
                                Pendiente: ${formatMoneyPunto(Math.max(0, totalOrden - (provisionExistente?.totalRecibido || 0)))}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </SoftBox>
                      )}
                    </SoftBox>

                    <SoftBox
                      role="tabpanel"
                      id="provision-tabpanel-1"
                      aria-labelledby="provision-tab-1"
                      hidden={activeTab !== 1}
                      p={{ xs: 1.25, sm: 1.5 }}
                    >
                      {activeTab === 1 &&
                        (detallesPedido.length > 0 ? (
                          <SoftBox
                            sx={{
                              overflowX: "auto",
                              border: "1px solid #E9EDF4",
                              borderRadius: "9px",
                              "& table": { minWidth: "720px" },
                              "& thead": { backgroundColor: "#F6F8FB" },
                              "& tbody tr:last-of-type td": { borderBottom: 0 },
                              "& tbody tr:hover": { backgroundColor: "#FAFBFD" },
                            }}
                          >
                            <DataTable
                              columns={pedidoColumns}
                              rows={getPedidoRows(detallesPedido, provisionExistente?.detalles)}
                            />
                          </SoftBox>
                        ) : (
                          <SoftBox py={4} textAlign="center">
                            <Icon sx={{ color: "#A7B0C0", fontSize: "2rem !important" }}>
                              inventory
                            </Icon>
                            <SoftTypography variant="body2" color="secondary" mt={1}>
                              No se encontraron productos para este pedido.
                            </SoftTypography>
                          </SoftBox>
                        ))}
                    </SoftBox>

                    <SoftBox
                      role="tabpanel"
                      id="provision-tabpanel-2"
                      aria-labelledby="provision-tab-2"
                      hidden={activeTab !== 2}
                      p={{ xs: 1.25, sm: 1.5 }}
                    >
                      {activeTab === 2 &&
                        (remitosProvision.length > 0 ? (
                          <SoftBox
                            sx={{
                              overflowX: "auto",
                              border: "1px solid #E9EDF4",
                              borderRadius: "9px",
                              "& table": { minWidth: "720px" },
                              "& thead": { backgroundColor: "#F6F8FB" },
                              "& tbody tr:last-of-type td": { borderBottom: 0 },
                              "& tbody tr:hover": { backgroundColor: "#FAFBFD" },
                            }}
                          >
                            <DataTable
                              columns={remitosColumns}
                              rows={getRemitoRows(remitosProvision)}
                            />
                          </SoftBox>
                        ) : (
                          <SoftBox py={4} textAlign="center">
                            <Icon sx={{ color: "#A7B0C0", fontSize: "2rem !important" }}>
                              receipt_long
                            </Icon>
                            <SoftTypography variant="body2" color="secondary" mt={1}>
                              No se han registrado remitos para esta orden.
                            </SoftTypography>
                          </SoftBox>
                        ))}
                    </SoftBox>
                  </SoftBox>
                </Grid>
              </Grid>
            )}
            {esTerminal && !loadingQuery && (
              <SoftBox
                mt={1.5}
                p={1.25}
                display="flex"
                gap={1.25}
                sx={{ ...sectionStyle, backgroundColor: "#F7FAFC" }}
              >
                <Icon sx={{ color: "#52657D" }}>verified</Icon>
                <SoftBox>
                  <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#24344D" }}>
                    {provisionExistente?.estado}
                  </SoftTypography>
                  <SoftTypography variant="caption" display="block" color="secondary">
                    Motivo: {cierreTerminal?.motivo || "Sin motivo informado"}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            )}
            {puedeCerrar && !puedeAnular && (estadoCerrable || estadoCompletable) && (
              <SoftBox display="flex" justifyContent="center" flexWrap="wrap" gap={1} mt={3}>
                {estadoCerrable && (
                  <SoftButton
                    variant="text"
                    color="uproPink"
                    disabled={loadingCierre || loadingCompletar}
                    onClick={cerrarEntregaParcial}
                  >
                    {loadingCierre ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Icon>assignment_return</Icon>
                    )}
                    &nbsp; Cerrar con entrega parcial
                  </SoftButton>
                )}
                {estadoCompletable && (
                  <SoftButton
                    variant="text"
                    color="uproPink"
                    disabled={loadingCompletar || loadingCierre}
                    onClick={marcarCompletoYCerrar}
                  >
                    {loadingCompletar ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Icon>done_all</Icon>
                    )}
                    &nbsp; Marcar completo y cerrar
                  </SoftButton>
                )}
              </SoftBox>
            )}
            {provisionExistente?.anulada && !loadingQuery ? (
              <SoftBox
                mt={1.5}
                p={1.5}
                display="flex"
                alignItems="flex-start"
                gap={1}
                sx={{
                  borderRadius: "10px",
                  border: "1px solid #F5C7CB",
                  backgroundColor: "#FFF5F5",
                }}
              >
                <Icon color="error" fontSize="small">
                  cancel
                </Icon>
                <SoftBox>
                  <SoftTypography variant="caption" color="error" fontWeight="bold">
                    ORDEN ANULADA
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary" display="block">
                    {provisionExistente?.motivoAnulacion}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ) : (
              puedeAnular && (
                <SoftBox display="flex" justifyContent="center" mt={1.25}>
                  <SoftButton
                    variant="text"
                    color="uproPink"
                    onClick={anularProvision}
                    disabled={!provisionExistente}
                  >
                    <Icon>cancel</Icon>&nbsp; Anular orden de provisión
                  </SoftButton>
                </SoftBox>
              )
            )}
          </SoftBox>
          <ModalHistorial
            open={openHistorial}
            handleClose={() => setOpenHistorial(false)}
            historial={provisionExistente?.historial || []}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalProvision.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  id: PropTypes.string,
  refetchParent: PropTypes.func,
  mode: PropTypes.string,
};
